import { DISTRIBUTION_PROCESS_STATUS } from "@/constants/distributionStatus";

/**
 * Sistema de cor do módulo.
 *
 * Regra: cor carrega significado, e só existe UM significado por região.
 *  - âmbar / verde / vermelho -> exclusivos de STATUS (pendente / aceita / rejeitada)
 *  - superfície com tom âmbar -> exclusivo de "precisa da sua ação" (pendente)
 *  - cor da loteria           -> exclusiva de identidade (faixa lateral e bolinha)
 *  - todo o resto             -> tokens neutros do tema (bg-card, bg-muted, border-border)
 *
 * Neutros usam os tokens do design system (app/globals.css) em vez de hex fixo,
 * então claro e escuro saem coerentes sem duplicar valores.
 */

export const BADGE_BASE =
  "inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-xs font-medium";

/** Badge neutro: contagens e metadados que não são status. */
export const BADGE_NEUTRAL = "border-border bg-muted text-muted-foreground";

/** Status — a única escala semântica da tela. */
export const BADGE_STATUS: Record<number, string> = {
  [DISTRIBUTION_PROCESS_STATUS.PENDING]:
    "border-amber-300 bg-amber-100 text-amber-800 dark:border-amber-800/70 dark:bg-amber-950/60 dark:text-amber-200",
  [DISTRIBUTION_PROCESS_STATUS.ACCEPTED]:
    "border-emerald-300 bg-emerald-100 text-emerald-800 dark:border-emerald-800/70 dark:bg-emerald-950/60 dark:text-emerald-200",
  [DISTRIBUTION_PROCESS_STATUS.REJECTED]:
    "border-rose-300 bg-rose-100 text-rose-800 dark:border-rose-800/70 dark:bg-rose-950/60 dark:text-rose-200",
};

export const badgeStatus = (statusId: number) =>
  BADGE_STATUS[statusId] ?? BADGE_NEUTRAL;

/**
 * Pré-venda é atributo, não status: contorno âmbar sem preenchimento, para não
 * disputar atenção com o badge de status ao lado.
 */
export const BADGE_PRE_SALE =
  "border-amber-400 bg-transparent text-amber-700 dark:border-amber-700 dark:text-amber-300";

/**
 * Superfícies.
 *
 * No dark mode o app usa #202020 como fundo de página e #2c2c2c nos cards. Os
 * tokens --card/--background do tema são MAIS escuros que esse fundo, o que
 * faria o card afundar em vez de subir — por isso o tom escuro é fixado aqui.
 */
export const SURFACE_CARD =
  "border-border bg-card dark:border-zinc-700 dark:bg-[#2c2c2c]";

/** Bloco embutido dentro de um card (ex.: "Informações"). */
export const SURFACE_INSET =
  "border-border bg-muted/60 dark:border-zinc-700 dark:bg-black/20";

/** Barra fixa que precisa cobrir o conteúdo rolando por baixo. */
export const SURFACE_STICKY =
  "border-border bg-zinc-100/95 dark:border-zinc-700 dark:bg-[#202020]/95";

/** Superfícies de card. O tom âmbar marca só o que ainda exige ação. */
export const CARD_SURFACE = {
  default: SURFACE_CARD,
  actionable:
    "border-amber-300 bg-amber-50 dark:border-amber-800/70 dark:bg-amber-950/40",
  selected:
    "border-primary bg-primary/5 ring-2 ring-primary/40 dark:border-primary dark:bg-primary/20 dark:ring-primary/60",
};

/** Chips de ícone dos cards de resumo. */
export const SUMMARY_ACCENT = {
  pending:
    "bg-amber-100 text-amber-700 dark:bg-amber-950/60 dark:text-amber-300",
  accepted:
    "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300",
  rejected: "bg-rose-100 text-rose-700 dark:bg-rose-950/60 dark:text-rose-300",
  neutral: "bg-muted text-muted-foreground",
};

/** Botões de ação positiva/negativa, consistentes em toda a tela. */
export const ACTION_BUTTON = {
  acceptSolid:
    "bg-emerald-600 text-white hover:bg-emerald-700 dark:bg-emerald-600 dark:hover:bg-emerald-500",
  acceptOutline:
    "border-emerald-300 bg-transparent text-emerald-700 hover:bg-emerald-50 hover:text-emerald-800 dark:border-emerald-800 dark:text-emerald-300 dark:hover:bg-emerald-950/40",
  rejectSolid: "bg-rose-600 text-white hover:bg-rose-700",
  rejectOutline:
    "border-rose-300 bg-transparent text-rose-700 hover:bg-rose-50 hover:text-rose-800 dark:border-rose-800 dark:text-rose-300 dark:hover:bg-rose-950/40",
  acceptGhost:
    "text-emerald-700 hover:bg-emerald-50 hover:text-emerald-800 dark:text-emerald-300 dark:hover:bg-emerald-950/40",
  rejectGhost:
    "text-rose-700 hover:bg-rose-50 hover:text-rose-800 dark:text-rose-300 dark:hover:bg-rose-950/40",
};

/** Plural correto — "1 cota", "2 cotas". */
export const plural = (count: number, singular: string, many: string) =>
  count === 1 ? singular : many;
